import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const CHAPTERS = [
  { title: "Commercial Focus", desc: "We work exclusively in commercial construction. The pace, standards, and stakeholders are what we know best." },
  { title: "Project Management", desc: "Structured scheduling, budgeting, and coordination keep every trade aligned from preconstruction to closeout." },
  { title: "On Time, On Quality", desc: "We measure success by projects delivered on schedule, on budget, and finished to a standard that lasts." },
];

const LEADERSHIP_POINTS = [
  "Business development & company building",
  "Mergers & acquisitions experience",
  "Commercial construction & project delivery",
];

export default function About() {
  const { content } = useContent();
  const about = content.about || {};

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gray-300" />
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-gray-400">About Us</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 max-w-4xl leading-[1.03]">
            A team that treats your project like their own.
          </h1>
        </div>
      </section>

      {/* Intro split */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-12 md:grid-cols-12 items-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="md:col-span-5">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
            {about.heading}
          </h2>
          <p className="mt-6 text-base text-gray-600 leading-relaxed">{about.para1}</p>
          <p className="mt-4 text-base text-gray-600 leading-relaxed">{about.para2}</p>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fadeUp} className="md:col-span-7">
          <div className="aspect-[16/10] overflow-hidden border border-gray-200">
            <img src={about.image1} alt="Construction team reviewing plans on site" className="w-full h-full object-cover" />
          </div>
        </motion.div>
      </section>

      {/* Numbered chapters */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 border-l border-gray-200">
          {CHAPTERS.map((v, i) => (
            <motion.div key={v.title} data-testid={`about-value-${i}`} initial="hidden" whileInView="show"
              viewport={{ once: true, amount: 0.3 }} custom={i} variants={fadeUp} className="p-10 border-r border-gray-200">
              <span className="font-heading text-gray-300 text-4xl font-bold">0{i + 1}</span>
              <h3 className="mt-4 font-heading text-xl font-semibold text-gray-900">{v.title}</h3>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Leadership */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-12 md:grid-cols-12 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <div className="aspect-[4/3] overflow-hidden border border-gray-200">
            <img src={about.image2} alt="Construction leadership on site" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-6 order-1 md:order-2">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-gray-300" />
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-gray-400">Leadership</span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">
            {about.leadership_heading}
          </h2>
          <p className="mt-5 text-base text-gray-600 leading-relaxed">{about.leadership}</p>
          <ul className="mt-7 space-y-3">
            {LEADERSHIP_POINTS.map((t) => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle size={20} weight="fill" className="text-gray-900 shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-white tracking-tight max-w-xl">
            Let's build something that works for your business.
          </h2>
          <Link to="/contact" data-testid="about-cta"
            className="group inline-flex items-center gap-2 bg-white text-gray-950 font-semibold px-7 py-4 rounded-sm hover:bg-gray-200 transition-colors duration-200 whitespace-nowrap">
            Start a Conversation
            <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
}
